import { useMemo, useRef } from "react";
import type { MouseEvent, ReactNode } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import type { Editor } from "@tiptap/core";
import { Dropcursor, Gapcursor, Placeholder, UndoRedo } from "@tiptap/extensions";
import { UniqueID } from "@tiptap/extension-unique-id";

import type { DocumentKind } from "@/api/db/connection";
import type { DocumentBlock } from "@/api/document/block";
import { A4_METRICS } from "@/api/document/paginate";
import type { TermTypography } from "@/api/document/render";

import { BlockHandle } from "./block-handle";
import { EditorFieldsContext, type EditorField, type EditorPattern } from "./fields-context";
import { FieldsSuggestion, type CreateFieldContext } from "./fields-suggestion";
import { LintExtension } from "./lint";
import {
	BoldMark,
	DocNode,
	FieldNode,
	HeadingNode,
	ImageNode,
	ItalicMark,
	ListItemNode,
	PageBreakNode,
	ParagraphNode,
	PatternNode,
	SignatureNode,
	SignatureRowNode,
	SignatureSlotNode,
	StepNode,
	TableCellNode,
	TableNode,
	TableRowNode,
	TextNode,
	UnderlineMark,
	VariantCardNode,
} from "./nodes";
import { PAGE_GAP_BAND, PaginationExtension } from "./pagination";
import { DocumentPageStyle } from "./page-style";
import { DocumentPatternSelection } from "./pattern-selection";
import { DocumentWire } from "./wire";

const IDENTIFIED_TYPES = [
	"heading",
	"paragraph",
	"listItem",
	"step",
	"table",
	"tableRow",
	"tableCell",
	"variantCard",
	"pageBreak",
	"signature",
	"signatureRow",
	"signatureSlot",
	"image",
	"pattern",
];

const IMAGE_FILE_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);

function imageFilesOf(transfer: DataTransfer | null) {
	if (transfer === null) {
		return [];
	}

	return [...transfer.files].filter((file) => IMAGE_FILE_TYPES.has(file.type));
}

export interface EditorMargins {
	top: number;
	right: number;
	bottom: number;
	left: number;
}

export function editorBlocks(editor: Editor) {
	return DocumentWire.toBlocks(editor.getJSON());
}

function paginationExtension(
	margins: EditorMargins | undefined,
	onPagesChange?: (pages: string[][]) => void,
) {
	if (margins === undefined) {
		return PaginationExtension.configure({ onPagesChange });
	}

	const { top, bottom, left } = margins;

	return PaginationExtension.configure({
		marginTop: top,
		marginBottom: bottom,
		marginLeft: left,
		usableHeight: A4_METRICS.pageHeight - top - bottom,
		onPagesChange,
	});
}

export function useDocumentEditor(input: {
	blocks: DocumentBlock[];
	fields: EditorField[];
	editable?: boolean;
	paginated?: boolean;
	margins?: EditorMargins;
	onUpdate?: (blocks: DocumentBlock[]) => void;
	onCreateField?: (context: CreateFieldContext) => void;
	onPagesChange?: (pages: string[][]) => void;
	onImageFiles?: (files: File[], pos: number | null) => void;
}) {
	const fieldsRef = useRef(input.fields);
	fieldsRef.current = input.fields;

	const imageFilesRef = useRef(input.onImageFiles);
	imageFilesRef.current = input.onImageFiles;

	return useEditor({
		editable: input.editable ?? true,
		editorProps: {
			handlePaste: (_view, event) => {
				const files = imageFilesOf(event.clipboardData);

				if (files.length === 0 || imageFilesRef.current === undefined) {
					return false;
				}

				imageFilesRef.current(files, null);

				return true;
			},
			handleDrop: (view, event) => {
				const files = imageFilesOf(event.dataTransfer);

				if (files.length === 0 || imageFilesRef.current === undefined) {
					return false;
				}

				const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });

				imageFilesRef.current(files, coords === null ? null : coords.pos);

				return true;
			},
		},
		extensions: [
			DocNode,
			TextNode,
			BoldMark,
			UnderlineMark,
			ItalicMark,
			ParagraphNode,
			HeadingNode,
			ListItemNode,
			StepNode,
			TableNode,
			TableRowNode,
			TableCellNode,
			VariantCardNode,
			PageBreakNode,
			ImageNode,
			PatternNode,
			FieldNode,
			SignatureNode,
			SignatureRowNode,
			SignatureSlotNode,
			UndoRedo,
			Dropcursor,
			Gapcursor,
			...(input.editable === false
				? []
				: [
						Placeholder.configure({
							includeChildren: true,
							placeholder: ({ node }) =>
								node.type.name === "paragraph" ? "Escreva ou insira um campo com /" : "",
						}),
					]),
			UniqueID.configure({ attributeName: "id", types: IDENTIFIED_TYPES }),
			LintExtension,
			FieldsSuggestion.configure({
				getFields: (editor) => {
					const patternId = DocumentPatternSelection.id(editor);

					return fieldsRef.current.filter(
						(field) =>
							field.patternId === undefined ||
							field.patternId === null ||
							field.patternId === patternId,
					);
				},
				onCreateField: input.onCreateField,
			}),
			...(input.paginated ? [paginationExtension(input.margins, input.onPagesChange)] : []),
		],
		content: DocumentWire.toDoc(input.blocks),
		onUpdate: ({ editor }) => input.onUpdate?.(editorBlocks(editor)),
	});
}

interface DocumentEditorProps {
	editor: Editor;
	fields: EditorField[];
	kind: DocumentKind;
	stylesheet: string;
	readOnly?: boolean;
	template?: boolean;
	margins?: EditorMargins;
	typography?: TermTypography;
	header?: ReactNode;
	footer?: ReactNode;
	patterns?: EditorPattern[];
}

export function DocumentEditor({
	editor,
	fields,
	kind,
	stylesheet,
	readOnly,
	template,
	margins,
	typography,
	header,
	footer,
	patterns = [],
}: DocumentEditorProps) {
	const context = useMemo(
		() => ({
			fields: new Map(fields.map((field) => [field.id, field])),
			template: template === true,
			readOnly: readOnly === true,
			patterns: new Map(patterns.map((pattern) => [pattern.id, pattern])),
		}),
		[fields, patterns, readOnly, template],
	);

	const handleTailMouseDown = (event: MouseEvent<HTMLDivElement>) => {
		if (readOnly === true) {
			return;
		}

		if (event.button !== 0 || !(event.target instanceof Element)) {
			return;
		}

		if (event.target !== event.currentTarget && !editor.view.dom.contains(event.target)) {
			return;
		}

		if (event.target.closest(".doc-table") !== null) {
			return;
		}

		const { doc } = editor.view.state;
		const dom = editor.view.nodeDOM(doc.resolve(0).posAtIndex(doc.childCount - 1));

		if (!(dom instanceof HTMLElement) || event.clientY <= dom.getBoundingClientRect().bottom) {
			return;
		}

		event.preventDefault();

		const last = doc.lastChild;

		if (last !== null && last.isTextblock && last.content.size === 0) {
			editor.commands.focus("end");

			return;
		}

		editor.chain().insertContentAt(doc.content.size, { type: "paragraph" }).focus("end").run();
	};

	return (
		<EditorFieldsContext.Provider value={context}>
			<style>{stylesheet}</style>
			<style>{EDITOR_STYLES}</style>
			{kind === "manual" && (
				<div
					data-component="document-editor"
					data-read-only={readOnly === true}
					className="doc-manual doc-editor"
				>
					<div className="doc-page doc-block-gutter relative" onMouseDown={handleTailMouseDown}>
						{header}
						<EditorContent editor={editor} />
						{footer}
						{readOnly !== true && <BlockHandle editor={editor} />}
					</div>
				</div>
			)}
			{kind === "term" && (
				<div
					data-component="document-editor"
					className="doc-term doc-editor doc-editor-term"
					data-read-only={readOnly === true}
					style={DocumentPageStyle.typography(typography)}
				>
					<div
						className="doc-a4-strip doc-block-gutter relative"
						style={DocumentPageStyle.editorMargins(margins)}
						onMouseDown={handleTailMouseDown}
					>
						{header}
						<EditorContent editor={editor} />
						{footer}
						{readOnly !== true && <BlockHandle editor={editor} />}
					</div>
				</div>
			)}
		</EditorFieldsContext.Provider>
	);
}

const EDITOR_STYLES = `.doc-editor .tiptap{outline:none;counter-reset:doc-step}
.doc-editor .tiptap p.is-empty::before{content:attr(data-placeholder);color:var(--muted-foreground);float:left;height:0;pointer-events:none}
.doc-editor li{display:list-item;list-style:disc;margin-left:1.3em}
.doc-editor .lint-flag{box-shadow:inset 3px 0 0 0 var(--destructive);border-radius:var(--radius-sm)}
.doc-editor .step{counter-increment:doc-step}
.doc-editor .step-title::before{content:counter(doc-step) ".";flex:none;white-space:nowrap}
.doc-editor .doc-table table{margin:0}
.doc-manual .doc-table{margin:.8em 0}
.doc-term .doc-table{margin:0 0 8pt}
.doc-editor .page-break{margin:28px -22px;padding:14px 0;background:var(--document-gap);border:none;position:relative}
.doc-editor .page-break::after{content:"quebra de página";position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:var(--text-xs);letter-spacing:var(--tracking-eyebrow);text-transform:uppercase;color:var(--muted-foreground)}
.doc-editor .page-break.ProseMirror-selectednode{outline:2px solid var(--ring)}
.doc-editor .step input::placeholder{color:var(--muted-foreground);font-weight:400}
.doc-editor-term{padding:24px 0;overflow-x:auto}
.doc-editor-term .doc-a4-strip{isolation:isolate;box-sizing:border-box;width:${A4_METRICS.pageWidth}px;margin:0 auto;background:var(--document-paper);box-shadow:var(--shadow-document);padding:var(--e-pad-top,${A4_METRICS.marginTop}px) var(--e-pad-right,${A4_METRICS.marginRight}px) 0 var(--e-pad-left,${A4_METRICS.marginLeft}px)}
.doc-editor-term .tiptap{display:flow-root;position:relative;z-index:0}
.doc-editor-term .react-renderer.node-signature{margin:28pt 0 0}
.doc-editor-term .react-renderer.node-signature>.signature{margin:0}
.doc-editor-term .signature .react-renderer.node-signatureRow+.react-renderer.node-signatureRow{margin-top:28pt}
.doc-editor-term .react-renderer.node-table{margin:0 0 8pt}
.doc-editor-term .react-renderer.node-table>.doc-table{margin:0}
.doc-editor-term .react-renderer.node-step{margin:0 0 8pt}
.doc-editor-term .react-renderer.node-step>.step{margin:0}
.doc-editor-term .react-renderer.node-image{margin:0 0 8pt}
.doc-editor-term .react-renderer.node-image>figure.doc-image{margin:0}
.doc-editor-term .page-gap{display:flex;flex-direction:column;margin:0 calc(-1 * var(--e-pad-right,${A4_METRICS.marginRight}px)) 0 calc(-1 * var(--e-pad-left,${A4_METRICS.marginLeft}px))}
.doc-editor-term .page-gap-band{height:${PAGE_GAP_BAND}px;background:var(--document-gap);box-shadow:var(--shadow-document-gap)}
.doc-editor-term .page-break{margin:14px calc(-1 * var(--e-pad-left,${A4_METRICS.marginLeft}px));background:var(--document-gap)}
.doc-editor-term .doc-float{position:absolute;z-index:-1;cursor:grab;touch-action:none;user-select:none}
.doc-editor-term .doc-float:active{cursor:grabbing}
.doc-editor-term .doc-float img{display:block;width:100%;height:100%;max-width:none;pointer-events:none}
.doc-editor-term .doc-float-selected{z-index:30;outline:2px solid var(--ring)}
.doc-editor .signature-row>[data-node-view-content-react],.doc-editor .signature-row .react-renderer.node-signatureSlot{display:contents}
.doc-editor .step-title input{color:inherit;font:inherit;padding:0;border:0}
.doc-editor .doc-image.ProseMirror-selectednode{outline:none}`;
